// Equipment module constants + helpers. Used by every equipment view +
// the /fueling public webform. Kept free of React + Supabase so it can be
// imported from anywhere including scripts.

// ── the shapes this module reads ────────────────────────────────────────────
//
// `service_intervals` and the maintenance-completion rows are operator-authored
// jsonb, so the types below name the keys THIS module reads and keep an index
// signature for the rest: every helper spreads its input (`{...iv}`) into its
// output, so the unread keys survive and a narrower type would silently claim
// they do not exist.

export type EquipmentCategory = {
  key: string;
  label: string;
  icon: string;
  color: string;
  bg: string;
  bd: string;
};

// One entry of equipment.service_intervals (jsonb). THIS module owns the type:
// it does the scheduling maths, and equipmentMaterials imports it rather than
// keeping a second declaration of the same rows.
//
// `kind` and `hours_or_km` are optional and nullable — and `hours_or_km` admits
// a string — because nothing constrains a jsonb blob. Every writer in this repo
// happens to store a parseInt-derived number and a 'hours'/'km' literal
// (EquipmentWebformsAdmin.addOne, scripts/import_equipment.cjs), but
// equipmentMaterials reads the column straight off the row and hands it to
// computeIntervalStatus, so the declaration states what the column guarantees
// (nothing) rather than what today's writers happen to produce.
//
// Consequently the helpers below coerce with `Number(...)` before arithmetic.
// That is not a new rule: `-`, `*`, `/`, `%` and the relational operators all
// apply ToNumber to their operands already, and `Number()` IS ToNumber, so the
// coercion names a step the JavaScript always took. Key building uses
// `String(...)` for the same reason — it is exactly the ToString that `+` with
// a string operand already performed.
export type ServiceInterval = {
  kind?: string | null;
  hours_or_km?: number | string | null;
  label?: string | null;
  tasks?: unknown[] | null;
  [key: string]: unknown;
};

export type IntervalCompletion = {
  kind?: string | null;
  interval?: number | null;
  reading_at_completion?: number | null;
  items_completed?: unknown[] | null;
  total_tasks?: number | null;
  team_member?: string | null;
  [key: string]: unknown;
};

// One row of computeIntervalStatus' output: the configured interval plus the
// four derived fields. `until_due` is null when there is no current reading —
// distance-to-due is unknowable then, and 0 would read as "due now".
export type IntervalStatus = ServiceInterval & {
  last_at_reading: number | null;
  last_satisfied_milestone: number | null;
  next_due: number;
  overdue: boolean;
  until_due: number | null;
};

// The cumulative partial-completion state carried on a due interval.
export type IntervalPartialState = {
  milestone: number;
  latestSnap: number;
  latestTeam: string | null;
  items_completed: unknown[];
  items_done: number;
  total: number;
};

export type DueInterval = ServiceInterval & {
  last_done_at: number | null;
  last_done_milestone: number | null;
  first_missed_at: number;
  current_milestone: number;
  missed_count: number;
  last_partial: {
    items_done: number;
    items_completed: unknown[];
    total: number;
    at_reading: number;
    team_member: string | null;
    milestone: number;
  } | null;
};

// The unified projection row: a due entry (spread from computeDueIntervals,
// plus next_due/until_due) or an upcoming one (spread from
// computeIntervalStatus). Declared as the union of both so a consumer must
// branch on `due` before reading the due-only metadata.
export type ProjectedServiceInterval =
  | (DueInterval & {due: true; next_due: number | null; until_due: number | null})
  | (IntervalStatus & {due: false});

// Equipment row fields the reading helpers read. The two current_* columns and
// the two per-fueling reading columns are addressed dynamically by tracking
// unit, so both pairs are declared.
export type EquipmentReadingRow = {
  tracking_unit?: string | null;
  current_hours?: number | string | null;
  current_km?: number | string | null;
};

export type FuelingReadingRow = {
  date?: string | null;
  hours_reading?: number | string | null;
  km_reading?: number | string | null;
};

// Number.isFinite's own contract exactly (it does NOT coerce, so a numeric
// string is already false there) — written as a type guard so the checker can
// follow the narrowing the JavaScript always relied on.
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export const EQUIPMENT_CATEGORIES: EquipmentCategory[] = [
  {key: 'tractors', label: 'Tractors', icon: '🚜', color: '#065f46', bg: '#ecfdf5', bd: '#a7f3d0'},
  {key: 'atvs', label: 'ATVs', icon: '🛵', color: '#1e40af', bg: '#eff6ff', bd: '#bfdbfe'},
  {key: 'hijets', label: 'Hijets', icon: '🛻', color: '#92400e', bg: '#fffbeb', bd: '#fde68a'},
  {key: 'mowers', label: 'Mowers', icon: '🌱', color: '#a16207', bg: '#fef9c3', bd: '#fde047'},
  {key: 'skidsteers', label: 'Skidsteers', icon: '🚧', color: '#7f1d1d', bg: '#fef2f2', bd: '#fca5a5'},
  {key: 'forestry', label: 'Forestry', icon: '🌲', color: '#065f46', bg: '#f0fdfa', bd: '#5eead4'},
];

export const CATEGORY_BY_KEY: Record<string, EquipmentCategory> = Object.fromEntries(
  EQUIPMENT_CATEGORIES.map((c): [string, EquipmentCategory] => [c.key, c]),
);

// Equipment program color (for nav cards, tabs, etc.). Slate/steel.
export const EQUIPMENT_COLOR = '#57534e';
export const EQUIPMENT_BG = '#fafaf9';
export const EQUIPMENT_BD = '#d6d3d1';

// Threshold in days without a fueling entry before HomeDashboard flags the
// piece of equipment. Ronnie's call 2026-04-23: 14 days.
export const MISSED_FUELING_DAYS = 14;

// Warranty expiring threshold (days).
export const WARRANTY_WINDOW_DAYS = 60;

// Compute the next-due reading for a given interval on an equipment given
// its current reading. Handles the divisor rule: if interval X has been
// implicitly completed because a bigger interval Y (where X | Y) was done
// at the same milestone, the effective "last done" shifts up.
//
// currentReading: int (hours or km on the machine right now)
// intervals: array of {hours_or_km, kind, label}
// completions: array of {interval, kind, completed_at, ...} sorted by completed_at desc
//
// Returns for each interval: {next_due, overdue, completed_at_last, days_since_last}
export function computeIntervalStatus(
  intervals: readonly ServiceInterval[] | null | undefined,
  completions: readonly IntervalCompletion[] | null | undefined,
  currentReading: number | null | undefined,
): IntervalStatus[] {
  if (!Array.isArray(intervals) || intervals.length === 0) return [];

  // Defer to aggregateCompletionsByMilestone for the snap + cumulative-partial
  // logic. The same union-of-items rule applies: maintenance split across
  // sessions toward the same milestone counts as one virtual full completion.
  // Completions with a missing reading_at_completion fall back to the current
  // reading (matches prior behavior for legacy data without a snapshot).
  const normalized = (completions || []).map((c) => ({
    ...c,
    reading_at_completion: isFiniteNumber(c?.reading_at_completion) ? c.reading_at_completion : currentReading,
  }));
  const {milestoneSatisfied, milestoneSatisfiedRaw} = aggregateCompletionsByMilestone(intervals, normalized);

  return intervals.map((iv) => {
    const key = String(iv.kind) + ':' + String(iv.hours_or_km);
    const interval = Number(iv.hours_or_km);
    const lastM = milestoneSatisfied.get(key) || 0; // already a multiple of iv.hours_or_km
    const lastR = milestoneSatisfiedRaw.get(key) || null;
    const nextDue = lastM > 0 ? lastM + interval : interval;
    const overdue = currentReading != null && currentReading > nextDue;
    // Round to 1 decimal to dodge float-subtraction garbage
    // (e.g. 550 - 509.3 producing 40.69999999999999).
    const untilDue = currentReading != null ? Math.round((nextDue - currentReading) * 10) / 10 : null;
    return {
      ...iv,
      last_at_reading: lastR,
      last_satisfied_milestone: lastM > 0 ? lastM : null,
      next_due: nextDue,
      overdue: !!overdue,
      until_due: untilDue,
    };
  });
}

// Does the machine need any service right now? Returns the soonest-overdue
// interval or null.
export function soonestDue(
  intervals: readonly ServiceInterval[] | null | undefined,
  completions: readonly IntervalCompletion[] | null | undefined,
  currentReading: number | null | undefined,
  windowHours = 50,
): IntervalStatus | null {
  const statuses = computeIntervalStatus(intervals, completions, currentReading);
  if (currentReading == null) return null;
  // Priority: overdue first, then within windowHours of next_due.
  //
  // Both branches sort ascending by `until_due` (= next_due - currentReading),
  // which is ONE consistent ordering across the due boundary: most overdue
  // (most negative) first, then least overdue, then soonest upcoming.
  //
  // The previous overdue comparator was
  //   currentReading - a.next_due - (currentReading - b.next_due)
  // which reduces algebraically to `b.next_due - a.next_due` — descending by
  // next_due, i.e. the LEAST overdue interval. With a reading of 250 and 50h
  // and 100h intervals never done, it surfaced the 100h service (150 late)
  // instead of the 50h one (200 late), understating the machine's worst need.
  // `?? 0` for the same reason as the upcoming sort below, and it is behaviour-
  // preserving rather than merely type-quieting: until_due is `number | null`
  // with no undefined, and JS subtraction already coerces null to 0, so this is
  // the identical ordering the .js shipped.
  const overdue = statuses.filter((s) => s.overdue).sort((a, b) => (a.until_due ?? 0) - (b.until_due ?? 0));
  if (overdue.length > 0) return overdue[0];
  // The `until_due != null` filter is what makes the comparator's operands
  // numbers; restated in the sort because the filter's narrowing does not
  // survive into the callback's parameter type.
  const upcoming = statuses
    .filter((s) => s.until_due != null && s.until_due <= windowHours)
    .sort((a, b) => (a.until_due ?? 0) - (b.until_due ?? 0));
  return upcoming[0] || null;
}

// Snap a completion reading to the nearest milestone of the given interval.
// E.g. interval=500, reading=968 → snaps to 1000 (32 away vs 468 away).
// Tie-break favors the previous milestone (treat as late completion of prior).
// This is the math fix for the 2026-04-25 scenario where a 500hr maintenance
// done at 968h was being treated as a 468h-late completion of the 500
// milestone, then immediately flagged as overdue at 1000h. Under snap-to-
// nearest, 968h satisfies the 1000h milestone, next due at 1500h.
// `interval` admits the raw jsonb widths because the divisor cascade below
// forwards `ServiceInterval.hours_or_km` straight into it. The isFiniteNumber
// guard is unchanged and already answered 0 for anything that is not a number,
// so widening the parameter records the values that always reached it.
function snapToNearestMilestone(
  reading: number | null | undefined,
  interval: number | string | null | undefined,
): number {
  if (!isFiniteNumber(reading) || reading <= 0) return 0;
  if (!isFiniteNumber(interval) || interval <= 0) return 0;
  const fwd = Math.ceil(reading / interval) * interval;
  const back = Math.floor(reading / interval) * interval;
  // Already on a milestone — return it.
  if (fwd === back) return fwd;
  return fwd - reading < reading - back ? fwd : back;
}

// Given the equipment's intervals + fueling history + the reading the team
// just entered, compute which intervals are DUE RIGHT NOW. Ronnie's logic
// (2026-04-23): require the team to enter current reading, then walk back
// through prior fuel-up history to find intervals whose milestones were
// passed but never ticked. Return an array sorted by most-missed first.
//
// Each completion is snapped to the nearest milestone of its interval — that
// becomes the "satisfied milestone". Next due = satisfied + interval. So a
// 500hr done at 968h satisfies the 1000h milestone (closer than 500h),
// scheduling next 500hr at 1500h. A 500hr done at 1100h still snaps to
// 1000 (closer than 1500), scheduling next at 1500h. A 500hr done at
// 750h (exactly midway) snaps to 500h via the conservative tie-break.
export function computeDueIntervals(
  intervals: readonly ServiceInterval[] | null | undefined,
  completions: readonly IntervalCompletion[] | null | undefined,
  currentReading: number | null | undefined,
): DueInterval[] {
  if (!Array.isArray(intervals) || intervals.length === 0) return [];
  // `Number(currentReading)` is hoisted into a binding and reused below. The
  // relational and arithmetic uses of `currentReading` further down already
  // coerced it the same way per operand, so this changes nothing at runtime —
  // it just states the coercion once.
  const reading = Number(currentReading);
  if (!Number.isFinite(reading) || reading <= 0) return [];

  // Group ALL completions (full + partial) by (interval, snapped milestone).
  // Within a group, the union of items_completed is what's been done toward
  // that milestone. If the union covers all tasks → virtual full completion.
  // This handles the real-world flow where maintenance spans sessions: e.g.
  // a 500hr partial at 440h (14/16) + a 500hr partial at 444h (the missing
  // 2 of 16, done after parts arrived) = full coverage of the 500h milestone.
  const {milestoneSatisfied, milestoneSatisfiedRaw, partialState} = aggregateCompletionsByMilestone(
    intervals,
    completions,
  );

  const due: DueInterval[] = [];
  for (const iv of intervals) {
    const key = String(iv.kind) + ':' + String(iv.hours_or_km);
    const interval = Number(iv.hours_or_km);
    // The snapped milestone drives next-due math; the raw reading is for display.
    const lastSnapped = milestoneSatisfied.get(key) || 0;
    const lastRaw = milestoneSatisfiedRaw.get(key) || 0;
    const firstMilestoneAfterLast = lastSnapped > 0 ? lastSnapped + interval : interval; // no completion ever — first due at the first milestone
    if (firstMilestoneAfterLast > reading) continue;
    const largestMilestoneAtOrBeforeCurrent = Math.floor(reading / interval) * interval;
    const missedCount = (largestMilestoneAtOrBeforeCurrent - firstMilestoneAfterLast) / interval + 1;
    // Partial display: the cumulative state of the highest UNSATISFIED milestone.
    const ps = partialState.get(key);
    const lastPartial =
      ps && ps.milestone > lastSnapped
        ? {
            items_done: ps.items_done,
            items_completed: ps.items_completed,
            total: ps.total,
            at_reading: ps.latestSnap,
            team_member: ps.latestTeam,
            milestone: ps.milestone,
          }
        : null;
    due.push({
      ...iv,
      last_done_at: lastRaw > 0 ? lastRaw : null,
      last_done_milestone: lastSnapped > 0 ? lastSnapped : null,
      first_missed_at: firstMilestoneAfterLast,
      current_milestone: largestMilestoneAtOrBeforeCurrent,
      missed_count: missedCount,
      last_partial: lastPartial,
    });
  }

  // Ascending — smallest interval first (50h before 600h before 1200h).
  return due.sort((a, b) => Number(a.hours_or_km) - Number(b.hours_or_km));
}

// Presentation projection for the fueling webform's unified "Service
// intervals" list. Merges computeDueIntervals (due membership + missed
// metadata) with computeIntervalStatus (next_due / until_due for every
// configured interval). Deliberately contains NO due/remaining/milestone
// math of its own — both inputs come from the locked helpers above.
//
// Returns one entry per configured interval — {due: true} entries spread
// from computeDueIntervals' output (plus next_due / until_due from
// computeIntervalStatus), {due: false} entries spread from
// computeIntervalStatus' output — in ONE global order: ascending checklist
// cadence. Due status, next_due, until_due, and missed counts are
// presentation facts and must NEVER control row order (PROD hotfix
// 2026-07-17: due-first + nearest-milestone-first ordering scrambled the
// 5065 and honda-atv-1 lists). Sort: kind (deterministic alphabetical, only
// relevant if mixed kinds ever appear defensively) → hours_or_km ascending →
// original configuration index as the stable tie-break for equal identities.
export function projectServiceIntervals(
  intervals: readonly ServiceInterval[] | null | undefined,
  completions: readonly IntervalCompletion[] | null | undefined,
  currentReading: number | null | undefined,
): ProjectedServiceInterval[] {
  if (!Array.isArray(intervals) || intervals.length === 0) return [];
  const due = computeDueIntervals(intervals, completions, currentReading);
  const statuses = computeIntervalStatus(intervals, completions, currentReading);
  const statusByKey = new Map(
    statuses.map((s): [string, IntervalStatus] => [String(s.kind) + ':' + String(s.hours_or_km), s]),
  );
  const dueKeys = new Set(due.map((d) => String(d.kind) + ':' + String(d.hours_or_km)));
  const dueEntries = due.map((d): ProjectedServiceInterval => {
    const s = statusByKey.get(String(d.kind) + ':' + String(d.hours_or_km));
    return {...d, due: true, next_due: s ? s.next_due : null, until_due: s ? s.until_due : null};
  });
  const upcoming = statuses
    .filter((s) => !dueKeys.has(String(s.kind) + ':' + String(s.hours_or_km)))
    .map((s): ProjectedServiceInterval => ({...s, due: false}));
  const cfgIndex = new Map<string, number>();
  intervals.forEach((iv, i) => {
    if (!iv) return;
    const k = String(iv.kind) + ':' + String(iv.hours_or_km);
    if (!cfgIndex.has(k)) cfgIndex.set(k, i);
  });
  const merged = [...dueEntries, ...upcoming];
  // Array.prototype.sort is stable, so equal identities (defensive duplicate
  // config rows) keep their pre-merge relative order.
  merged.sort((a, b) => {
    const kindCmp = String(a.kind).localeCompare(String(b.kind));
    if (kindCmp !== 0) return kindCmp;
    // The inequality test stays on the RAW values: it has always been a strict
    // identity check, so a configured 500 and a configured '500' are still two
    // identities here even though the ordering subtraction coerces both.
    if (a.hours_or_km !== b.hours_or_km) return Number(a.hours_or_km) - Number(b.hours_or_km);
    const ia = cfgIndex.get(String(a.kind) + ':' + String(a.hours_or_km));
    const ib = cfgIndex.get(String(b.kind) + ':' + String(b.hours_or_km));
    return (ia != null ? ia : 0) - (ib != null ? ib : 0);
  });
  return merged;
}

// Internal helper used by both computeDueIntervals and computeIntervalStatus.
// Aggregates completions by (interval, snapped milestone) and decides which
// milestones are satisfied (full or virtual full from cumulative partials).
//
// A "virtual full" happens when the union of items_completed across all
// completions in the same milestone group covers the interval's task count.
// This handles the real-world flow where maintenance is split across multiple
// sessions before the next milestone hits.
//
// Returns:
//   milestoneSatisfied:    Map<kind:value, milestoneNumber>
//   milestoneSatisfiedRaw: Map<kind:value, latestRawReading>
//   partialState:          Map<kind:value, {milestone, latestSnap, latestTeam,
//                                            items_completed, items_done, total}>
type MilestoneGroup = {kind: string; interval: number; milestone: number; entries: IntervalCompletion[]};

function aggregateCompletionsByMilestone(
  intervals: readonly ServiceInterval[],
  completions: readonly IntervalCompletion[] | null | undefined,
): {
  milestoneSatisfied: Map<string, number>;
  // The raw map's value is `number | undefined` rather than `number` because
  // the divisor cascade below re-inserts whatever `get(parentKey)` returned.
  // Every writer sets both maps together so a miss cannot happen today, but
  // declaring `number` here would be a promise this function does not enforce,
  // and both readers already spell `|| 0` / `|| null` for exactly that case.
  milestoneSatisfiedRaw: Map<string, number | undefined>;
  partialState: Map<string, IntervalPartialState>;
} {
  function isFullCompletion(c: IntervalCompletion): boolean {
    if (!c.total_tasks || c.total_tasks === 0) return true;
    const count = Array.isArray(c.items_completed) ? c.items_completed.length : 0;
    return count >= c.total_tasks;
  }

  // Authoritative task count per interval — uses CURRENT equipment config so
  // historical completions get re-evaluated against today's task list.
  const currentTotals = new Map<string, number>();
  for (const iv of intervals) {
    currentTotals.set(String(iv.kind) + ':' + String(iv.hours_or_km), Array.isArray(iv.tasks) ? iv.tasks.length : 0);
  }

  // Group by (kind, interval, snapped milestone).
  const groups = new Map<string, MilestoneGroup>();
  for (const c of completions || []) {
    if (!c || !c.interval || !c.kind) continue;
    const snap = isFiniteNumber(c.reading_at_completion) ? c.reading_at_completion : null;
    if (snap == null) continue;
    const milestone = snapToNearestMilestone(snap, c.interval);
    const key = c.kind + ':' + c.interval + ':' + milestone;
    let group = groups.get(key);
    if (!group) {
      group = {
        kind: c.kind,
        interval: c.interval,
        milestone,
        entries: [],
      };
      groups.set(key, group);
    }
    group.entries.push(c);
  }

  const milestoneSatisfied = new Map<string, number>();
  const milestoneSatisfiedRaw = new Map<string, number | undefined>();
  const partialState = new Map<string, IntervalPartialState>();

  for (const g of groups.values()) {
    const ivKey = g.kind + ':' + g.interval;
    // currentTotals may be 0 for an interval that has no sub-tasks (parent-tick only).
    const configuredTotal = currentTotals.get(ivKey);
    const total = configuredTotal !== undefined ? configuredTotal : (g.entries[0] && g.entries[0].total_tasks) || 0;

    const allItems = new Set<unknown>();
    let hasFullEntry = false;
    let latestSnap = 0,
      latestTeam: string | null = null;
    for (const e of g.entries) {
      const items = Array.isArray(e.items_completed) ? e.items_completed : [];
      for (const id of items) allItems.add(id);
      if (isFullCompletion(e)) hasFullEntry = true;
      if (e.reading_at_completion != null && e.reading_at_completion > latestSnap) {
        latestSnap = e.reading_at_completion;
        latestTeam = e.team_member || null;
      }
    }

    // For sub-tasks check the union; for parent-tick-only intervals (total=0)
    // a single full entry is enough.
    const isCumulativeFull = hasFullEntry || (total > 0 && allItems.size >= total);

    if (isCumulativeFull) {
      const ex = milestoneSatisfied.get(ivKey);
      if (!ex || g.milestone > ex) {
        milestoneSatisfied.set(ivKey, g.milestone);
        milestoneSatisfiedRaw.set(ivKey, latestSnap);
      }
    } else {
      const ex = partialState.get(ivKey);
      if (!ex || g.milestone > ex.milestone) {
        partialState.set(ivKey, {
          milestone: g.milestone,
          latestSnap,
          latestTeam,
          items_completed: Array.from(allItems),
          items_done: allItems.size,
          total,
        });
      }
    }
  }

  // Divisor rule: a satisfied parent milestone implicitly satisfies any
  // sub-interval that divides it. Cascade the parent's RAW reading to each
  // sub-interval; each sub does its own independent snap.
  for (const ivKey of Array.from(milestoneSatisfied.keys())) {
    const rawOfParent = milestoneSatisfiedRaw.get(ivKey);
    const [kind, vStr] = ivKey.split(':');
    const parentInterval = parseInt(vStr, 10);
    for (const iv of intervals) {
      if (iv.kind !== kind) continue;
      if (iv.hours_or_km === parentInterval) continue;
      if (parentInterval % Number(iv.hours_or_km) !== 0) continue;
      const subKey = String(iv.kind) + ':' + String(iv.hours_or_km);
      const subSnapped = snapToNearestMilestone(rawOfParent, iv.hours_or_km);
      const ex = milestoneSatisfied.get(subKey);
      if (!ex || subSnapped > ex) {
        milestoneSatisfied.set(subKey, subSnapped);
        milestoneSatisfiedRaw.set(subKey, rawOfParent);
      }
    }
  }

  return {milestoneSatisfied, milestoneSatisfiedRaw, partialState};
}

// Strip Podio HTML tags (and trivial "None" placeholder) from comment text.
// Podio stored comments as rendered HTML like "<p>None</p>" or "<p>Did not
// do checklist</p>"; we want clean plain text in the UI.
export function stripPodioHtml(s: unknown): string | null {
  if (s == null) return null;
  const clean = String(s)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean || clean.toLowerCase() === 'none' || clean === 'N/A' || clean.toLowerCase() === 'n/a') return null;
  return clean;
}

// Days since an ISO date. Returns null for falsy/invalid input.
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date((iso + '').slice(0, 10) + 'T12:00:00').getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}

// Pretty-print a reading with unit. 1234 hours → "1,234 hr"; 5000 km → "5,000 km".
export function fmtReading(value: number | string | null | undefined, unit: string | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  // Math.round already performs this coercion on a numeric string; naming it
  // makes the tolerated string input visible instead of implicit.
  return Math.round(Number(value)).toLocaleString() + ' ' + (unit === 'km' ? 'km' : 'hr');
}

// Derive an effective current reading for an equipment row. Originally a
// drift-compensation helper: anon-context updates to equipment.current_*
// from the public fueling webform were silently failing under prod RLS
// (recon 2026-04-28), and HomeDashboard's overdue-interval math was running
// against stale parent rows. Mig 047 (submit_equipment_fueling SECURITY
// DEFINER RPC, 2026-05-06) fixes the write path AND ships a one-shot
// reconciliation; new submissions correctly bump equipment.current_*. The
// helper stays in place as defensive fallback — historical pre-mig-047
// rows + any future caller that bypasses the RPC are still tolerated by
// this read-side derivation.
//
// Rule:
//   1. Pick the latest fueling by date for this piece.
//   2. If that fueling's reading (in the equipment's tracking unit) is
//      strictly greater than equipment.current_*, use it.
//   3. Otherwise fall back to equipment.current_* (admin-controlled value
//      stays authoritative when it's ahead of any anon submission, e.g.
//      after a manual reading correction).
//   4. No fuelings → equipment.current_*.
//
// Fuelings array shape: each entry has {date, hours_reading?, km_reading?}.
// Order is irrelevant — helper picks the max-date row internally.
export function latestSaneReading(
  eq: EquipmentReadingRow | null | undefined,
  fuelings: readonly FuelingReadingRow[] | null | undefined,
): number {
  const unit = eq?.tracking_unit === 'km' ? 'km' : 'hours';
  const readingCol = unit === 'km' ? 'km_reading' : 'hours_reading';
  const currentCol = unit === 'km' ? 'current_km' : 'current_hours';
  const currentReading = Number(eq?.[currentCol]);
  if (!Array.isArray(fuelings) || fuelings.length === 0) return currentReading;
  const latest = fuelings.reduce<FuelingReadingRow | null>(
    (m, f) => (!m || (f?.date || '') > (m?.date || '') ? f : m),
    null,
  );
  const latestReading = Number(latest?.[readingCol]);
  // When equipment.current_* is blank (null/undefined), Number() returns NaN.
  // Without the !isFinite-current check we'd return NaN even though we have a
  // valid latest fueling reading. Treat blank current_* as "no admin floor"
  // and let the latest fueling reading stand.
  if (Number.isFinite(latestReading) && (!Number.isFinite(currentReading) || latestReading > currentReading)) {
    return latestReading;
  }
  return currentReading;
}

// Recompute the equipment's current meter reading from its fueling history.
// Used by the admin fuel-log editor after correcting/deleting a historical
// reading. Public submissions advance equipment.current_* through the RPC,
// but admin corrections must also be allowed to move the parent reading down
// when a typo was previously saved too high.
export function currentReadingFromFuelings(
  eq: EquipmentReadingRow | null | undefined,
  fuelings: readonly FuelingReadingRow[] | null | undefined,
): number | null {
  const unit = eq?.tracking_unit === 'km' ? 'km' : 'hours';
  const readingCol = unit === 'km' ? 'km_reading' : 'hours_reading';
  if (!Array.isArray(fuelings) || fuelings.length === 0) return null;

  let max: number | null = null;
  for (const f of fuelings) {
    const reading = Number(f?.[readingCol]);
    if (!Number.isFinite(reading) || reading <= 0) continue;
    if (max == null || reading > max) max = reading;
  }
  return max;
}
